/**
 * The driver registry in the Admin (W9-DRIVER-REGISTRY, D481, ADR-0099) — framework-neutral view-models for the
 * registration grid, the registration form, the record's readiness checklist and evidence, and the central document query,
 * over the `driver-registry-routes.ts` surface.
 *
 * The console only FILTERS and EXPLAINS: it hides write actions from whoever lacks `driver:register`, turns a stable reason
 * code into a copy id (`registry.reason.*`) and every refusal into words. The server is the authority on all of it — the
 * operator is always the session identity. Neutral: a country's own fields arrive as a described form and are rendered
 * generically; no country, document or language literal lives here.
 */
import type { DriverReadinessView, DriverRegistryFormView, ReadinessLineStatusView, ReadinessStateView, RegisterDriverInput, RegistryAddress, RegistryFieldSpecView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type RegistryTone = 'neutral' | 'success' | 'warning' | 'danger';

/** Who may do what (display only — the server re-authorizes every call). */
export function driverRegistryAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayRegister: boolean; readonly mayViewDocuments: boolean } {
  if (grants === undefined) return { mayView: true, mayRegister: true, mayViewDocuments: true };
  return {
    mayView: grantsAllow(grants, { permission: 'driver:view', target: 'GEO' }),
    mayRegister: grantsAllow(grants, { permission: 'driver:register', target: 'GEO' }),
    mayViewDocuments: grantsAllow(grants, { permission: 'document:view', target: 'GEO' }),
  };
}

export const READINESS_STATE_TONE: Readonly<Record<ReadinessStateView, RegistryTone>> = { READY: 'success', ACTION_REQUIRED: 'warning', IN_REVIEW: 'neutral', SUSPENDED: 'danger' };
export const READINESS_LINE_TONE: Readonly<Record<ReadinessLineStatusView, RegistryTone>> = { OK: 'success', ACTION_REQUIRED: 'warning', IN_REVIEW: 'neutral', BLOCKED: 'danger' };

/** The copy id and params of a stable readiness reason (`DOCUMENT_MISSING:IDENTITY`, `ONBOARDING_MISSING:SELFIE`, `VEHICLE_NOT_INSPECTED`). */
export function readinessReasonCopy(reason: string): { readonly id: string; readonly params: Readonly<Record<string, string>> } {
  const [code, arg] = reason.split(':');
  const known = /^[A-Z][A-Z0-9_]*$/.test(code ?? '') ? (code as string) : 'UNKNOWN';
  if (known.startsWith('ONBOARDING_')) return { id: `registry.reason.${known}`, params: { item: arg ?? '' } };
  if (known.startsWith('DOCUMENT_')) return { id: `registry.reason.${known}`, params: { type: arg ?? '' } };
  return { id: `registry.reason.${known}`, params: {} };
}

/** Whether a copy id of a reason is one the catalog knows (an unknown stable code falls back to its words-free form). */
export const READINESS_REASON_CODES: readonly string[] = [
  'ACCOUNT_NOT_ACTIVE', 'ACCOUNT_SUSPENDED', 'ONBOARDING_MISSING', 'ONBOARDING_REJECTED',
  'DOCUMENT_MISSING', 'DOCUMENT_EXPIRED', 'DOCUMENT_REJECTED', 'DOCUMENT_PENDING_REVIEW', 'DOCUMENT_EXPIRY_UNKNOWN',
  'VEHICLE_NOT_INSPECTED', 'VEHICLE_CATEGORY_MISMATCH', 'VEHICLE_INSPECTION_EXPIRED', 'VEHICLE_NOT_ACTIVE', 'VEHICLE_TOO_OLD', 'VEHICLE_RULE_NOT_SET',
  'KYC_CATALOG_NOT_SET', 'KYC_REQUIREMENTS_NOT_DEFINED', 'DRIVER_RATING_TOO_LOW', 'DRIVER_TRIPS_TOO_FEW',
];

/** The copy id of a readiness reason, or `registry.reason.UNKNOWN` when the catalog does not name its code. */
export function readinessReasonId(reason: string): string {
  const { id } = readinessReasonCopy(reason);
  return READINESS_REASON_CODES.includes(id.slice('registry.reason.'.length)) ? id : 'registry.reason.UNKNOWN';
}

// ---- the registration form -------------------------------------------------------------------------------------------------------

export interface RegistrationDraft {
  readonly name: string;
  readonly phone: string;
  readonly locale: string;
  readonly email: string;
  readonly mapIdentifier: string;
  readonly observation: string;
  readonly birthDate: string;
  readonly gender: '' | 'FEMALE' | 'MALE' | 'OTHER';
  readonly genderConsent: boolean;
  readonly line1: string;
  readonly line2: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
  readonly groupId: string;
  /** The country's own fields, by key. */
  readonly attributes: Readonly<Record<string, string>>;
  /** D723: a rider's driver invitation code (optional) and the category it is for (required with a code). */
  readonly referralCode: string;
  readonly referralCategoryCode: string;
}

export function emptyRegistrationDraft(locale: string): RegistrationDraft {
  return { name: '', phone: '', locale, email: '', mapIdentifier: '', observation: '', birthDate: '', gender: '', genderConsent: false, line1: '', line2: '', locality: '', region: '', postalCode: '', groupId: '', attributes: {}, referralCode: '', referralCategoryCode: '' };
}

/** The country fields that are shown under the current values (a `showWhen` field appears only when its trigger holds). */
export function visibleCountryFields(fields: readonly RegistryFieldSpecView[], values: Readonly<Record<string, string>>): readonly RegistryFieldSpecView[] {
  return fields.filter((f) => f.showWhen === undefined || f.showWhen.in.includes(values[f.showWhen.field] ?? ''));
}

const trimmed = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim());

/** The address of a draft, or undefined when none of its lines is filled. */
export function draftAddress(d: Pick<RegistrationDraft, 'line1' | 'line2' | 'locality' | 'region' | 'postalCode'>): RegistryAddress | undefined {
  const filled = [d.line1, d.line2, d.locality, d.region, d.postalCode].some((v) => v.trim() !== '');
  if (!filled) return undefined;
  const line2 = trimmed(d.line2);
  const region = trimmed(d.region);
  const postalCode = trimmed(d.postalCode);
  return { line1: d.line1.trim(), locality: d.locality.trim(), ...(line2 !== undefined ? { line2 } : {}), ...(region !== undefined ? { region } : {}), ...(postalCode !== undefined ? { postalCode } : {}) };
}

/** The request body of a draft: only what is filled, the gender with its consent, the country fields that are shown. */
export function registrationInput(marketId: string, d: RegistrationDraft, form: Pick<DriverRegistryFormView, 'countryFields'> | undefined): RegisterDriverInput {
  const shown = form?.countryFields !== undefined ? visibleCountryFields(form.countryFields.fields, d.attributes) : [];
  const attributes = Object.fromEntries(shown.map((f) => [f.key, (d.attributes[f.key] ?? '').trim()]).filter(([, v]) => v !== ''));
  const address = draftAddress(d);
  const email = trimmed(d.email);
  const mapIdentifier = trimmed(d.mapIdentifier);
  const observation = trimmed(d.observation);
  const birthDate = trimmed(d.birthDate);
  const groupId = trimmed(d.groupId);
  return {
    marketId,
    name: d.name.trim(),
    phone: d.phone.trim(),
    locale: d.locale,
    ...(email !== undefined ? { email } : {}),
    ...(mapIdentifier !== undefined ? { mapIdentifier } : {}),
    ...(observation !== undefined ? { observation } : {}),
    ...(birthDate !== undefined ? { birthDate } : {}),
    ...(d.gender !== '' ? { gender: d.gender, genderConsent: d.genderConsent } : {}),
    ...(address !== undefined ? { address } : {}),
    ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
    ...(groupId !== undefined ? { groupId } : {}),
    // D723: the invitation code travels with its category, upper-cased as the rider shared it.
    ...(trimmed(d.referralCode) !== undefined ? { referralCode: d.referralCode.trim().toUpperCase(), referralCategoryCode: d.referralCategoryCode.trim() } : {}),
  };
}

/** Whether the draft may be sent: a name, a phone, and every shown required country field filled. */
export function registrationReady(d: RegistrationDraft, form: Pick<DriverRegistryFormView, 'countryFields'> | undefined): boolean {
  if (d.name.trim() === '' || d.phone.trim() === '' || d.locale.trim() === '') return false;
  if (d.gender !== '' && !d.genderConsent) return false;
  const anyAddress = [d.line1, d.line2, d.locality, d.region, d.postalCode].some((v) => v.trim() !== '');
  if (anyAddress && (d.line1.trim() === '' || d.locality.trim() === '')) return false;
  if (d.referralCode.trim() !== '' && d.referralCategoryCode.trim() === '') return false; // D723: a code needs its category
  const shown = form?.countryFields !== undefined ? visibleCountryFields(form.countryFields.fields, d.attributes) : [];
  return shown.every((f) => !f.required || (d.attributes[f.key] ?? '').trim() !== '');
}

/** The label of a country field/option in the operator's language (the pack's own copy), falling back to English, then the key. */
export function packLabel(labels: Readonly<Record<string, string>> | undefined, lang: string, fallbackKey: string): string {
  if (labels === undefined) return fallbackKey;
  return labels[lang] ?? labels.en ?? Object.values(labels)[0] ?? fallbackKey;
}

/** The readiness of a record, ready for the checklist (the lines in order, required first). */
export function checklistLines(r: DriverReadinessView): DriverReadinessView['lines'] {
  return [...r.lines].sort((a, b) => Number(b.requirement === 'REQUIRED') - Number(a.requirement === 'REQUIRED'));
}
